/** The first route from the URL (docs/spec/06 §6). */

import { caveSet, findCave, START_CAVES } from '../caves';
import { decodeCave } from '../caves/codec';
import { expeditionCave } from '../caves/generator';
import type { Route } from '../ui/scene';
import { num, pick, query } from './params';

const SCREENS = ['title', 'practice', 'daily', 'expedition', 'options', 'controls', 'hiscores', 'help', 'credits', 'kit'] as const;

export function bootRoute(): Route {
  if (query.has('demo')) return { to: 'play', request: { mode: 'demo' } };
  const code = query.get('cave');
  if (code !== null && code.startsWith('SF')) {
    // A cave shared from the Construction Kit: play it, then the kit has it for remixing (?edit=1 goes there first).
    const shared = decodeCave(code);
    if (shared.ok) {
      if (query.has('edit')) return { to: 'kit', def: shared.def };
      return { to: 'play', request: { mode: 'practice', def: shared.def, level: 1, back: { to: 'title', skipPress: true } } };
    }
    console.warn(`stonefall: that cave link is damaged (${shared.reason})`);
  }
  const play = query.get('play');
  const level = Math.min(5, Math.max(1, Math.round(num('level', 1))));
  if (play === 'classic' || play === 'deep') {
    const letter = (query.get('cave') ?? 'A').toUpperCase();
    // Any cave letter works here (not just A, E, I, M), which is what testing wants.
    if (findCave(caveSet(play), letter)) {
      return { to: 'play', request: { mode: play, players: query.get('players') === '2' ? 2 : 1, startCave: letter, level } };
    }
    return { to: 'play', request: { mode: play, players: 1, startCave: START_CAVES[0] ?? 'A', level } };
  }
  if (query.has('depth')) {
    // One generated cave on its own, for looking at the generator: /?seed=3&depth=7
    const def = expeditionCave(Math.round(num('seed', 1)), Math.max(1, Math.round(num('depth', 1))));
    return { to: 'play', request: { mode: 'practice', def, level: 1, back: { to: 'title' } } };
  }
  if (play === 'expedition') return { to: 'play', request: { mode: 'expedition', seed: Math.round(num('seed', 1)) } };
  if (play === 'daily') {
    const d = new Date();
    return { to: 'play', request: { mode: 'daily', year: d.getUTCFullYear(), month: d.getUTCMonth() + 1, day: d.getUTCDate() } };
  }
  if (play === 'practice') {
    const set = pick('set', ['classic', 'deep'] as const, 'classic');
    const def = findCave(caveSet(set), (query.get('cave') ?? 'A').toUpperCase());
    if (def) return { to: 'play', request: { mode: 'practice', def, level, back: { to: 'practice' } } };
  }
  const screen = query.get('screen');
  if (screen === 'setup') return { to: 'setup', set: pick('set', ['classic', 'deep'] as const, 'classic') };
  if (screen && (SCREENS as readonly string[]).includes(screen)) return { to: screen as (typeof SCREENS)[number] };
  return { to: 'title' };
}
